import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import attendanceService from '../../services/attendanceService';
import classesService from '../../services/classesService';
import {
  DataTable,
  FormInput,
  FormSelect,
} from '../../components/finance';
import { Plus, Pencil, Download, X, CheckCircle, XCircle, Clock, FileText } from '@phosphor-icons/react';
import './TeacherAttendanceSection.css';

function TeacherAttendanceSection({ taughtClasses, academicTerms = [], selectedAcademicTerm, onTermChange }) {
  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const today = getTodayDate();
  
  const [selectedClass, setSelectedClass] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(today);
  const [attendance, setAttendance] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [termSelected, setTermSelected] = useState(selectedAcademicTerm);

  // Load attendance for selected class and date
  const loadAttendance = (classId, date) => {
    if (!classId || !date) return;
    
    setLoading(true);
    attendanceService.getClassAttendance(classId, date)
      .then(data => {
        setAttendance(data.records || []);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load attendance');
      })
      .finally(() => setLoading(false));
  };

  // Load students for class
  const loadClassStudents = (classId) => {
    classesService.getClassStudents(classId)
      .then(data => {
        setClassStudents(data.items || []);
        // Initialize bulk data
        setBulkData((data.items || []).map(s => ({
          student_id: s.id,
          student_name: `${s.first_name} ${s.last_name}`,
          status: 'present',
        })));
      })
      .catch(err => console.error(err));
  };

  // Handle mark attendance — if the student already has a record for this date,
  // update it instead of creating a new one (the create endpoint rejects a
  // second record for the same day, so re-clicking a different status for an
  // already-marked student used to just fail with no visible change).
  const handleMarkAttendance = async (studentId, status, existingRecord) => {
    try {
      if (!termSelected) {
        toast.error('Please select an academic term');
        return;
      }
      const today = getTodayDate();
      if (attendanceDate !== today) {
        toast.error('Attendance can only be marked for today');
        return;
      }

      if (existingRecord?.attendance_id) {
        await attendanceService.updateAttendance(existingRecord.attendance_id, status.toLowerCase());
      } else {
        await attendanceService.markAttendance({
          student_id: studentId,
          class_id: selectedClass,
          attendance_date: attendanceDate,
          academic_term_id: termSelected,
          status: status.toLowerCase(),
        });
      }
      toast.success(`Attendance marked as ${status}`);
      loadAttendance(selectedClass, attendanceDate);
    } catch (err) {
      console.error(err);
      toast.error('Failed to mark attendance');
    }
  };

  // Handle bulk mark attendance
  const handleBulkMarkAttendance = async (e) => {
    e.preventDefault();
    
    try {
      if (!termSelected) {
        toast.error('Please select an academic term');
        return;
      }
      const today = getTodayDate();
      if (attendanceDate !== today) {
        toast.error('Attendance can only be marked for today');
        return;
      }

      const payload = {
        class_id: selectedClass,
        attendance_date: attendanceDate,
        academic_term_id: termSelected,
        records: bulkData.map(item => ({
          student_id: item.student_id,
          status: item.status,
        }))
      };

      await attendanceService.bulkMarkAttendance(payload);
      toast.success('Attendance marked for all students');
      setShowBulkModal(false);
      loadAttendance(selectedClass, attendanceDate);
    } catch (err) {
      console.error(err);
      toast.error('Failed to mark bulk attendance');
    }
  };

  const getStatusBadgeColor = (status) => {
    const lowerStatus = (status || '').toLowerCase();
    switch (lowerStatus) {
      case 'present':
        return '#10B981';
      case 'absent':
        return '#EF4444';
      case 'late':
        return '#F59E0B';
      case 'excused':
        return '#8B5CF6';
      default:
        return '#6B7280';
    }
  };

  return (
    <div className="teacher-attendance-section">
      <div className="section-header">
        <h2>Mark Attendance</h2>
        <button className="btn btn-primary" onClick={() => {
          if (!selectedClass) {
            toast.error('Please select a class first');
            return;
          }
          setShowBulkModal(true);
        }}
        >
          <Plus size={18} />
          Bulk Mark
        </button>
      </div>

      {/* Class & Date Selection */}
      <div className="filters-container">
        {/* Academic Term Selection */}
        {academicTerms && academicTerms.length > 0 && (
          <div className="filter-group">
            <label>Academic Term</label>
            <select
              value={termSelected || ''}
              onChange={(e) => {
                const termId = e.target.value;
                setTermSelected(termId);
                if (onTermChange) onTermChange(termId);
              }}
              className="select-input"
            >
              <option value="">Select Academic Term</option>
              {academicTerms.map(term => (
                <option key={term.id} value={term.id}>
                  {term.term} - {term.academic_year}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label>Class</label>
          <select
            value={selectedClass || ''}
            onChange={(e) => {
              const classId = e.target.value;
              setSelectedClass(classId);
              loadClassStudents(classId);
              loadAttendance(classId, attendanceDate);
            }}
            className="select-input"
          >
            <option value="">Select a class</option>
            {taughtClasses.map(cls => (
              <option key={cls.class_id} value={cls.class_id}>
                {cls.class_name} 
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Date</label>
          <input
            type="date"
            value={attendanceDate}
            onChange={(e) => {
              const selectedDate = e.target.value;
              const today = getTodayDate();
              
              // Only allow today's date
              if (selectedDate !== today) {
                toast.error('Attendance can only be marked for today');
                setAttendanceDate(today);
                return;
              }
              
              setAttendanceDate(selectedDate);
              if (selectedClass) {
                loadAttendance(selectedClass, selectedDate);
              }
            }}
            max={getTodayDate()}
            className="input-date"
          />
          <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
            Attendance can only be marked for today
          </small>
        </div>

        <div className="filter-group">
          <label>&nbsp;</label>
          <button 
            className="btn btn-secondary"
            disabled
            style={{ opacity: 0.5, cursor: 'not-allowed' }}
            title="Date is locked to today"
          >
            Today
          </button>
        </div>
      </div>

      {/* Attendance Table */}
      {selectedClass && (
        <div className="attendance-table-container">
          {loading ? (
            <div className="loading">Loading attendance...</div>
          ) : classStudents.length > 0 ? (
            <div className="attendance-grid">
              {classStudents.map(student => {
                const studentAttendance = attendance.find(a => a.student_id === student.id);
                return (
                  <div key={student.id} className="attendance-card">
                    <div className="student-info">
                      <p className="student-name">{student.first_name} {student.last_name}</p>
                      <p className="student-id">{student.student_id}</p>
                    </div>

                    <div className="status-display">
                      {studentAttendance ? (
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusBadgeColor(studentAttendance.status) }}
                        >
                          {studentAttendance.status?.charAt(0).toUpperCase() + studentAttendance.status?.slice(1)}
                        </span>
                      ) : (
                        <span className="status-badge" style={{ backgroundColor: '#9ca3af' }}>
                          Not Marked
                        </span>
                      )}
                    </div>

                    <div className="status-buttons">
                      <button
                        className="status-btn present"
                        onClick={() => handleMarkAttendance(student.id, 'present', studentAttendance)}
                        title="Mark Present"
                      >
                        <span className="btn-icon"><CheckCircle size={18} /></span>
                        <span className="btn-label">Present</span>
                      </button>
                      <button
                        className="status-btn absent"
                        onClick={() => handleMarkAttendance(student.id, 'absent', studentAttendance)}
                        title="Mark Absent"
                      >
                        <span className="btn-icon"><XCircle size={18} /></span>
                        <span className="btn-label">Absent</span>
                      </button>
                      <button
                        className="status-btn late"
                        onClick={() => handleMarkAttendance(student.id, 'late', studentAttendance)}
                        title="Mark Late"
                      >
                        <span className="btn-icon"><Clock size={18} /></span>
                        <span className="btn-label">Late</span>
                      </button>
                      <button
                        className="status-btn excused"
                        onClick={() => handleMarkAttendance(student.id, 'excused', studentAttendance)}
                        title="Mark Excused"
                      >
                        <span className="btn-icon"><FileText size={18} /></span>
                        <span className="btn-label">Excused</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>No students in this class</p>
            </div>
          )}
        </div>
      )}

      {/* Bulk Mark Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => {
          setShowBulkModal(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bulk Mark Attendance</h2>
              <button 
                className="modal-close-btn"
                onClick={() => setShowBulkModal(false)}
                type="button"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleBulkMarkAttendance} className="bulk-form">
              <div className="bulk-students">
                {bulkData.map((item, idx) => (
                  <div key={item.student_id} className="bulk-item">
                    <span className="student-label">{item.student_name}</span>
                    <select
                      value={item.status}
                      onChange={(e) => {
                        const newBulkData = [...bulkData];
                        newBulkData[idx].status = e.target.value;
                        setBulkData(newBulkData);
                      }}
                      className="form-input"
                    >
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                      <option value="late">Late</option>
                      <option value="excused">Excused</option>
                    </select>
                  </div>
                ))}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Mark All
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherAttendanceSection;
